import {
  TeamRole, TeamIdentity,
  BillingCycleType,
  CoreApplicationContext,
  AgentOperationIdentifier,
  IItemPartitionedByTeamIdBase,
  QuotaUsage
} from "../../models/coreModels";
import { IItemBase } from "../../models/baseModels";

export enum AuthProvider {
  Apple = 0,
  Google = 1,
  Microsoft = 2,
  Test = 3,  // Test-only provider for automated testing
  Email = 4  // Email + password sign-in for users with a stored password hash
}

export enum ApiResultType {
  Ok,
  Failed,
  NotFound,
  Unauthorized,
  InvalidRequest,
  QuotaLimit,
  NoInvite
}

export interface ApiResponseBase {
  result: ApiResultType;
}

export interface ApiFailureResponse extends ApiResponseBase {
  result: ApiResultType.Failed;
}

export interface ApiResponse extends ApiResponseBase {
  result: ApiResultType.Ok;
}

export interface ApiDataResponse<TData> extends ApiResponse {
  data: TData;
}

export interface ExchangeTokenRequest {
  idToken: string;
  provider: AuthProvider;
  inviteCode: string | null;
}

export interface ExchangeTokenResponse {
  isNewSignUp: boolean;
  inviteRequired: boolean;
  coreApplicationContext: CoreApplicationContext | null;
}

export interface ValidateInviteRequest {
  inviteCode: string;
}

export interface ValidateInviteResponse {
  isValid: boolean;
}

export interface AuthenticateTestUserRequest {
  emailAddress: string;
  password: string;
}

export interface AuthenticateTestUserResponse {
  token: string;
}

export interface AuthenticateEmailPasswordUserRequest {
  emailAddress: string;
  password: string;
}

export interface AuthenticateEmailPasswordUserResponse {
  token: string;
}

export interface ValidateAuthSessionResponse {
  authenticated: boolean;
}

// MFE (micro-frontend) token scoping. The main app exchanges its full session for a
// short-lived (20 min) read-only app JWT, then hands it to the MFE which establishes a
// host-only read-only session on the MFE origin.
export interface GetMfeTokenResponse {
  token: string;
}

export interface CreateMfeSessionRequest {
  token: string;
}

export interface ItemIdResponse {
  id: string;
}

export interface CreateNewTeamRequest {
  newTeamName: string;
}

export interface UpdateTeamNameRequest {
  newTeamName: string;
}

export interface InviteReference {
  inviteCode: string;
  emailAddress: string;
}

export interface RemoveTeamInviteRequest {
  inviteReference: InviteReference;
}

export interface InviteTeamMemberRequest {
  memberEmailAddress: string;
  teamRole: TeamRole;
}

export interface ChangeActiveTeamRequest {
  newActiveTeamIdentity: TeamIdentity;
}

export type GenerativeRequestBase = {
  contentId: string;
  applicationContext: string | null;
  inputModel: any; // Supports both string dictionaries and complex objects for Handlebars templates
}

export type GenerativeResponseBase = {
  message: string;
}

export type BillingCycleData = {
  type: BillingCycleType;
  periodEnd: string;
}

export enum SubscriptionStatus {
  Inactive,
  Active,
  ActiveButCancellingAtPeriodEnd
}
export interface SubscriptionDataResponse {
  status: SubscriptionStatus;
  currentBillingCycle: BillingCycleData | null;
  quotaUsage: QuotaUsage;
}

export interface CreateSubscriptionCheckoutRequest {
  productId: string;
  priceId: string;
  userAddOnPriceId: string;
  userAddOnQuantity: number;
}

export interface CreateSubscriptionCheckoutResponse {
  clientSecret: string;
}

export interface ChangeAccountPlanRequest {
  newPriceId: string;
}

export interface ChangeAccountPlanAddOnRequest {
  addOnProductId: string;
  addOnQuantity: number;
}

export interface UpdateSubscriptionCheckoutSessionRequest {
  sessionId: string;
}

export interface UpdateSubscriptionCheckoutSessionResponse {
  status: string;
  customerId: string;
  customerEmail: string;
  coreApplicationContext: any; // TODO: Import proper CoreApplicationContext type
}

export interface GenerateMcpTokenResponse {
  accessToken: string;
  teamId: string;
  teamName: string;
  expiresIn: number;
}

// Base Integration Models
// All OAuth-based integrations (HubSpot, Stripe, etc.) share this common pattern

export enum OAuthDestination {
  Connections = 0,
  FirstRunExperience = 1
}

export interface ConnectIntegrationRequestBase {
  destination?: OAuthDestination;
}

export interface ConnectIntegrationResponseBase {
  authorizationUrl: string;
}

export interface CompleteConnectIntegrationRequestBase {
  state: string;
  code: string;
}

export interface SyncScheduleResponse {
  cronExpression: string;
  isEnabled: boolean;
}

export interface IntegrationStatusResponseBase {
  isConnected: boolean;
  canBeConfigured: boolean;
  isConfigured: boolean;
  connectedAt: string | null;
  requiresReauthorization: boolean;
  syncSchedule: SyncScheduleResponse | null;
}

// GitHub Integration Models

export interface ConnectGitHubAppResponse {
  installationUrl: string;
}

export interface CompleteConnectGitHubAppRequest {
  state: string;
  installationId: string;
}

export interface InitiateConnectGitHubAppVerifiedResponse {
  authUrl: string;
}

export interface CompleteConnectGitHubAppVerifiedRequest {
  code: string;
  state: string;
  installationId: string;
}

export interface InitiateConnectExistingResponse {
  authUrl: string;
}

export interface CompleteConnectExistingRequest {
  code: string;
  state: string;
}

export interface DiscoveredInstallation {
  installationId: string;
  accountLogin: string;
  accountAvatarUrl: string;
}

export interface DiscoverInstallationsResponse {
  installations: DiscoveredInstallation[];
}

export interface ConnectDiscoveredInstallationRequest {
  installationId: string;
}

export interface GitHubRepository {
  fullName: string;
  name: string;
}

export interface GitHubBranch {
  name: string;
}

export interface GitHubRepoConfig {
  repositoryFullName: string;
  selectedBranchName: string | null;
  contextFilenames: string[];
  isManaged: boolean;
  isContextSyncEnabled: boolean;
}

export interface GitHubConnection {
  connectedAt: string;
  repositories: GitHubRepoConfig[];
  availableRepositories: GitHubRepository[];
  selectedRepository: GitHubRepository | null;
  availableBranches: GitHubBranch[];
  selectedBranch: GitHubBranch | null;
  contextFilenames: string[];
}

export interface ManagedOrgInfo {
  orgLogin: string;
  provisionedAt: string;
}

export interface GitHubStatusResponse {
  connection: GitHubConnection | null;
  managedOrg: ManagedOrgInfo | null;
}

export interface GetRepositoryBranchesResponse {
  branches: GitHubBranch[];
}

export interface SetGitHubRepositoryRequest {
  repositoryFullName: string;
}

export interface SetGitHubBranchRequest {
  branchName: string;
}

export interface ProvisionManagedRepoRequest {
  name: string;
}

export interface ProvisionManagedRepoResponse {
  repositoryFullName: string;
}

export interface LinkGitHubAccountResponse {
  authUrl: string;
}

export interface CompleteLinkGitHubAccountRequest {
  code: string;
  state: string;
}

// HubSpot Integration
export interface HubSpotStatusResponse extends IntegrationStatusResponseBase {
  tokenIsExpired: boolean;
  scopes: string[];
  authorizedScopes: string[];
}

// Stripe Integration
export interface StripeStatusResponse extends IntegrationStatusResponseBase {
  stripeUserId?: string;
}

// Salesforce Integration
export interface SalesforceStatusResponse extends IntegrationStatusResponseBase {
  clientId: string;
  clientSecret: string;
  instanceUrl: string | null;
}

export interface SalesforceConfigureRequest {
  clientId: string;
  clientSecret: string;
}

// NetSuite Integration
export interface NetSuiteConfigureRequest {
  accountId: string;
  consumerKey: string;
  certificateId: string;
}

export interface NetSuiteStatusResponse extends IntegrationStatusResponseBase {
  accountId: string | null;
  consumerKey: string | null;
  certificateId: string | null;
  hasPrivateKey: boolean;
}

// QuickBooks Integration
export interface CompleteConnectQuickBooksRequest extends CompleteConnectIntegrationRequestBase {
  realmId: string;
}

export interface QuickBooksStatusResponse extends IntegrationStatusResponseBase {
  tokenIsExpired: boolean;
  companyId: string | null;
}

// Shopify Integration
export interface ShopifyConfigureRequest {
  shopDomain: string;
  clientId: string;
  clientSecret: string;
}

export interface ShopifyConnectRequest {
  shopDomain: string;
  destination: number;
}

export interface CompleteConnectShopifyRequest extends CompleteConnectIntegrationRequestBase {
  shop: string;
}

export interface ShopifyStatusResponse extends IntegrationStatusResponseBase {
  tokenIsExpired: boolean;
  usesCustomCredentials: boolean;
  clientId: string;
  clientSecret: string;
  shopDomain: string | null;
}

// Maxio Integration
export interface MaxioConfigureRequest {
  apiKey: string;
  subdomain: string;
  accountId: string;
}

export interface MaxioStatusResponse extends IntegrationStatusResponseBase {
  apiKey: string;
  subdomain: string;
  accountId: string;
}

// Recurly Integration
export interface RecurlyConfigureRequest {
  apiKey: string;
  region: string;
}

export interface RecurlyStatusResponse extends IntegrationStatusResponseBase {
  apiKey: string;
  region: string;
}

// Meta Integration
export interface MetaStatusResponse extends IntegrationStatusResponseBase {
  metaUserId: string | null;
  selectedBusinessIds: string[];
  selectedAdAccountIds: string[];
}

export interface MetaAdAccountInfo {
  id: string;
  accountId: string;
  name: string;
  currency: string;
  accountStatus: number;
}

export interface MetaBusinessGroup {
  businessId: string;
  businessName: string;
  adAccounts: MetaAdAccountInfo[];
}

export interface MetaAdAccountListResponse {
  businesses: MetaBusinessGroup[];
}

export interface UpdateMetaAdAccountSelectionRequest {
  selectedAdAccountIds: string[];
}

// Maxio Advanced Billing Integration
export interface MaxioAdvancedBillingConfigureRequest {
  apiKey: string;
  subdomain: string;
}

export interface MaxioAdvancedBillingStatusResponse extends IntegrationStatusResponseBase {
  apiKey: string;
  subdomain: string;
}

// Stay.ai Integration
export interface StayAiConfigureRequest {
  apiKey: string;
}

export interface StayAiStatusResponse extends IntegrationStatusResponseBase {
  apiKey: string;
}

// Google Ads Integration
export interface GoogleAdsStatusResponse extends IntegrationStatusResponseBase {
  googleAdsCustomerId: string | null;
}

// Google Drive Integration
export interface GoogleDriveStatusResponse extends IntegrationStatusResponseBase {
  selectedFileCount: number;
  // Folder scoping: when restrictToSelectedFolder is true and selectedFolderId is null, the
  // user has connected but must still pick a source folder before selecting files. Older
  // connections report restrictToSelectedFolder=false (any-file access).
  restrictToSelectedFolder: boolean;
  selectedFolderId: string | null;
  selectedFolderName: string | null;
}

export enum TabularFileFormat {
  Csv = 0,
  Xlsx = 1,
  GoogleSheet = 2
}

export interface GoogleDriveSelectedFile {
  alias: string;
  displayName: string;
  sourceFileId: string;
  format: TabularFileFormat;
  mimeType: string | null;
}

export interface GetGoogleDriveSelectedFilesResponse {
  files: GoogleDriveSelectedFile[];
  // Mirrors GoogleDriveStatusResponse so the file-selection UI can drive the "pick a folder
  // first" flow and scope the Picker from this one query.
  restrictToSelectedFolder: boolean;
  selectedFolderId: string | null;
  selectedFolderName: string | null;
}

export interface GoogleDriveSelectedFileRequest {
  sourceFileId: string;
  name: string;
  mimeType: string;
}

export interface SetGoogleDriveSelectedFilesRequest {
  files: GoogleDriveSelectedFileRequest[];
}

export interface SetGoogleDriveSelectedFolderRequest {
  folderId: string;
  folderName: string;
}

export interface GoogleDrivePickerConfigResponse {
  apiKey: string;
  clientId: string;
  appId: string;
  oauthToken: string;
  scope: string;
}

/**
 * Intake Amazon DSP — an independent Google Drive OAuth connection whose configuration is a single
 * vendor folder rather than a file selection.
 */
export interface IntakeAmazonDspStatusResponse extends IntegrationStatusResponseBase {
  // Null until the user has picked the vendor's folder: connected, but nothing to ingest yet.
  selectedFolderId: string | null;
  selectedFolderName: string | null;
  // The most recent daily export already ingested (yyyy-MM-dd); empty before the first successful sync.
  ingestionCursor: string;
}

export interface SetIntakeAmazonDspFolderRequest {
  folderId: string;
  folderName: string;
}

// TikTok Ads Integration
export interface TikTokAdsStatusResponse extends IntegrationStatusResponseBase {
  advertiserId: string | null;
}

// Amazon Ads Integration (Sponsored Products / Brands / Display)
export interface AmazonAdsStatusResponse extends IntegrationStatusResponseBase {
  tokenIsExpired: boolean;
}

// TikTok Seller Integration (TikTok Shop)
export interface TikTokSellerStatusResponse extends IntegrationStatusResponseBase {
  tokenIsExpired: boolean;
  sellerName: string | null;
}

// Amazon DSP Integration (Demand-Side Platform display advertising)
export interface AmazonDspStatusResponse extends IntegrationStatusResponseBase {
  tokenIsExpired: boolean;
}

// Amazon Seller Integration (Selling Partner API)
// Region must match the C# AmazonSellerRegion enum exactly. Only North America is wired
// up; EU and Far East can be added later by extending both this enum and the C# one.
export enum AmazonSellerRegion {
  NorthAmerica = 0
}

export interface ConnectAmazonSellerRequest {
  region: AmazonSellerRegion;
  destination: number;
}

export interface CompleteConnectAmazonSellerRequest extends CompleteConnectIntegrationRequestBase {
  sellingPartnerId: string;
}

export interface AmazonSellerStatusResponse extends IntegrationStatusResponseBase {
  tokenIsExpired: boolean;
  region: string | null;
  sellingPartnerId: string | null;
  marketplaceIds: string[];
}

// Connector Sync Types
export interface RecordFieldSchema {
  fieldName: string;
  dataType: string;
  label: string;
  description: string;
  isRequired: boolean;
  // Optional Synapse string-column sizing hint. Only consulted when dataType is a string
  // type. Null uses the default sized NVARCHAR; positive emits NVARCHAR(N); zero or
  // negative emits NVARCHAR(MAX) for long-form text columns.
  maxLength: number | null;
}

export interface ConnectorSchema {
  recordTypeName: string;
  fields: RecordFieldSchema[];
  label: string;
  description: string;
}

export interface BronzeSchemaTableInfo {
  recordTypeName: string;
  bronzeViewName: string;
}

export interface BronzeSchemaSourceInfo {
  sourceType: string;
  bronzeSourceName: string;
  tables: BronzeSchemaTableInfo[];
}

export interface BronzeSchemaContextResponse {
  bronzeSchemaName: string;
  sources: BronzeSchemaSourceInfo[];
}

export interface ConnectorSchemaRecord extends IItemPartitionedByTeamIdBase {
  source: string;
  schema: ConnectorSchema;
  changeNumber: number;
}

export enum ImportStatus {
  Queued = 0,
  SourceIngestionInProgress = 1,
  BronzeIngestionInProgress = 2,
  GoldCreationInProgress = 4,
  Completed = 5,
  Failed = 6
}

export interface TableSummary {
  name: string;
  recordCount: number;
}

export interface ImportProgress {
  recordType: string;
  recordsFetched: number;
  recordsStored: number;
  status: ImportStatus;
  errorMessage: string | null;
  progressPercentage: number;
}

export interface ImportResult extends IItemBase {
  teamId: string;
  sourceType: string;
  status: ImportStatus;
  recordsFetched: number;
  recordsStored: number;
  changeNumber: number;
  progress: number;
  startedAt: string | null;
  completedAt: string | null;
  operationId: string;
  errorMessage: string | null;
  tableSummaries: TableSummary[] | null;
  recordTypeProgress: ImportProgress[] | null;
  goldPipelineBaselines: Record<string, number> | null;
  // Canonical sanitized bronze source identifier for sourceType (e.g. "hubspot",
  // "my_cool_data") — the key the gold pipeline status per-source data is keyed by.
  sourceKey: string;
  // Wire identifier for connector HTTP routes ({serviceKey} path segments): the kebab-case
  // service key for standard connectors, or the connection name for custom connections.
  serviceKey: string;
}

export enum GoldPipelineStage {
  Transforming = 0,
  WritingToStorage = 1,
  RunningDatabricksJob = 2,
  Completed = 4,
  Failed = 5,
  Cancelled = 6,
  Queued = 7
}

export interface GoldPipelineStatusResponse {
  id: string;
  changeNumber: number;
  objectTypes: string[];
  recordsTransformed: number;
  specsExecuted: number;
  totalSpecs: number;
  startedAt: string;
  completedAt: string | null;
  status: ImportStatus;
  stage: GoldPipelineStage;
  specTimings: Record<string, number>;
  errorMessage: string | null;
  isReadyForUse: boolean;
  isFullRun: boolean;
  consumedSourceChangeNumbers: Record<string, number>;
  dirtySources: string[];
  referencedSources: string[] | null;
  executedSpecs: string[];
  copiedSpecs: string[];
  lastSuccessfulConsumedSourceChangeNumbers: Record<string, number>;
}

export interface IngestionRecord {
  id: string;
  teamId: string;
  source: string;
  changeNumber: number;
  recordType: string;
  properties: Record<string, any>;
}

export interface QueueAgentOperationResponse {
  operationIdentifier: AgentOperationIdentifier;
}

export interface PaginatedResult<T> {
  items: T[];
  totalCount: number;
  skip: number;
  take: number;
}
// Databricks Data Query Types
export interface GetDatabricksDataRequest {
  /** The table name (e.g., "customers", "orders") */
  tableName: string;
  /** Optional limit on the number of rows returned (default: 100) */
  limit?: number | null;
  /** Optional offset for pagination */
  offset?: number | null;
  /** Optional column to order by */
  orderBy?: string | null;
  /** Sort direction (true = DESC, false = ASC) */
  orderDescending?: boolean;
}

export interface DatabricksSqlColumnInfo {
  name: string;
  typeName: string;
  typeText: string;
}

export interface DatabricksSqlQueryResult {
  state: string;
  columns: DatabricksSqlColumnInfo[];
  rows: (string | null)[][] | null;
  totalRowCount: number;
}

export interface ExecuteSqlQueryRequest {
  query: string;
  schemaType: string;
  limit: number;
}

// Custom Connections
export interface StartCustomConnectionUploadRequest {
  fileName: string;
  name?: string | null;
  connectionId?: string | null;
}

export interface StartCustomConnectionUploadResponse {
  uploadId: string;
  connectionId: string;
  uploadUrl: string;
  expiresAt: string;
  fileExtension: string;
}

export interface CompleteCustomConnectionUploadRequest {
  connectionId: string;
  fileExtension: string;
  name?: string | null;
}

/** Matches CustomFileCursorDataTypes.Supported in backend-core/applicationlayer/customfiles. */
export type CustomConnectionCursorDataType = 'datetime' | 'date' | 'integer' | 'number' | 'string';

/** One column of a record type's incremental-import cursor. */
export interface CustomConnectionCursorColumn {
  column: string;
  dataType: CustomConnectionCursorDataType;
}

/** Matches CustomConnectionImportModes in backend-core/datalayer/models/CustomConnectionModels.cs. */
export type CustomConnectionImportMode = 'append' | 'replaceAndAppend';

/** One record type's incremental-import configuration: its mode and the cursor that orders it. */
export interface CustomConnectionRecordTypeIncrementalImport {
  /**
   * 'append' ingests only the rows past the last committed cursor; 'replaceAndAppend' ingests
   * every row, replacing previously ingested data from the file's lowest cursor tuple onward and
   * keeping the history below it.
   */
  mode: CustomConnectionImportMode;
  /**
   * The record type's cursor — an ordered tuple of typed columns. Array order is significant:
   * the cursor compares by the first column, ties broken by the second, and so on.
   */
  cursorColumns: CustomConnectionCursorColumn[];
}

/**
 * Key: full record type name. A record type absent from the map is imported in full (replaced)
 * on every sync.
 */
export interface CustomConnectionIncrementalImport {
  recordTypes: Record<string, CustomConnectionRecordTypeIncrementalImport>;
}

export interface UpdateCustomConnectionRequest {
  name?: string | null;
  // null = leave unchanged; non-null = full replacement; empty object = clear.
  incrementalImport?: CustomConnectionIncrementalImport | null;
}

export interface CustomConnectionColumn {
  /** The bronze column name — what a cursor column and any SQL over the table reference. */
  name: string;
  /** The header cell's text exactly as it appears in the file. */
  header: string;
}

/** One record type of a custom file: a CSV's single sheet, or one worksheet of an XLSX. */
export interface CustomConnectionSheet {
  recordTypeName: string;
  /** The worksheet's name as it appears in the file; empty for a CSV. */
  sheetName: string;
  columns: CustomConnectionColumn[];
}

export interface CustomConnectionResponse {
  id: string;
  name: string;
  fileExtension: string;
  /** Known from the moment the file is uploaded — no sync required. */
  sheets: CustomConnectionSheet[];
  incrementalImport: CustomConnectionIncrementalImport | null;
}

// Connector Source Settings
export interface ConnectorSourceSettings {
  enabledRecordTypes: string[];
  additionalRecordTypes: string[];
  parameters: Record<string, string>;
}

/** Matches ConnectorParameterKind in backend-core/applicationlayer/ingestion/ISourceParameterCapable.cs */
export enum ConnectorParameterKind {
  /** Comma-separated integers. An empty value means unset. */
  IntegerList = 0,
  /** "true" or "false". An empty or absent value means false. */
  Boolean = 1
}

export interface ConnectorParameterDescriptor {
  key: string;
  label: string;
  description: string;
  kind: ConnectorParameterKind;
}

export interface ConnectorSettingsResponse {
  settings: ConnectorSourceSettings;
  availableRecordTypes: string[];
  availableParameters: ConnectorParameterDescriptor[];
  connectorType: string;
}

// Validation
export enum ValidationRunStage {
  Loading = 0,
  Running = 1,
  UpdatingStatus = 2,
  Completed = 3,
  Failed = 4
}

export interface ValidationRunSummary {
  id: string;
  dataPipelineConfigurationId: string;
  startedAt: string;
  completedAt: string | null;
  status: ImportStatus;
  stage: ValidationRunStage;
  totalTestCases: number;
  testCasesCompleted: number;
  passed: number;
  failed: number;
  errors: number;
}

export interface ValidationTestCase {
  id: string;
  name: string;
  query: string;
  expectedResult: number;
  widgetId: string | null;
  kpiDefinitionId: string | null;
  source: string;
  type: string;
  category: string;
}

export interface ValidationRunTestCaseResult {
  testCaseId: string;
  name: string;
  query: string;
  expectedResult: number;
  actualResult: number | null;
  widgetId: string | null;
  kpiDefinitionId: string | null;
  category: string;
  status: string;
  errorMessage: string | null;
}

export interface ValidationRun {
  id: string;
  dataPipelineConfigurationId: string;
  startedAt: string;
  completedAt: string | null;
  status: ImportStatus;
  stage: ValidationRunStage;
  totalTestCases: number;
  testCasesCompleted: number;
  passed: number;
  failed: number;
  errors: number;
  errorMessage: string | null;
  results: ValidationRunTestCaseResult[];
}

export interface ValidationSuiteDetail {
  testCases: ValidationTestCase[];
  latestRun: ValidationRun | null;
}

export interface ContextFileStatus {
  fileName: string;
  status: 'Default' | 'Custom' | 'Missing';
}

export interface ContextStatusResponse {
  files: ContextFileStatus[];
}

// ---------------------------------------------------------------------------
// OpsAppObjects stateful store — team-scoped objects written by custom
// dashboard view/widget code. Contracts mirror api/endpoints/ops-app-objects/Models.cs.
// ---------------------------------------------------------------------------

/** Values are flat primitives — string, number, boolean, or null; datetime/date fields hold ISO8601 strings. Every declared field is present, though its value may be null. */
export type OpsAppObjectValue = string | number | boolean | null;

export type OpsAppObjectFieldType = "string" | "integer" | "number" | "boolean" | "date" | "datetime";

export interface OpsAppObjectFieldDefinition {
  name: string;
  type: OpsAppObjectFieldType;
}

export interface OpsAppObjectTypeDefinition {
  /** The object type's unique id: its snake_case table name. */
  tableName: string;
  fields: OpsAppObjectFieldDefinition[];
  sequenceField: string;
  /** Monotone schema version, incremented whenever fields are added (the only permitted change). */
  versionNumber: number;
}

export interface OpsAppObjectDefinitionsResponse {
  objectTypes: OpsAppObjectTypeDefinition[];
  /** Server-resolved: Editor+ role AND a token carrying the objects-write scope. */
  canWrite: boolean;
}

export interface OpsAppObject {
  id: string;
  tableName: string;
  changeNumber: number;
  /** Integral ordering key, driven by the type's sequenceField. */
  sequenceNumber: number;
  properties: Record<string, OpsAppObjectValue>;
  lastUpdated: string;
}

export interface OpsAppObjectListResponse {
  /** Ordered by sequenceNumber. */
  items: OpsAppObject[];
  totalCount: number;
  skip: number;
  take: number;
}

export interface UpsertOpsAppObjectRequest {
  properties: Record<string, OpsAppObjectValue>;
}

export interface DeleteOpsAppObjectResponse {
  deleted: boolean;
}
